const ROUND_TYPES = [
  'first_double',
  'first_half',
  'time_zones',
  'double_jeopardy',
  'wildcard',
  'all_or_nothing',
  'pyramid',
  'length_matters',
  'category_lockout',
];

export const SPECIAL_ROUND_TITLES = {
  first_double: '2x Everything',
  first_half: 'Half Points',
  time_zones: 'Speed Bonus',
  double_jeopardy: 'Double Jeopardy',
  wildcard: 'Wildcard',
  all_or_nothing: 'All or Nothing',
  pyramid: 'Streak Pyramid',
  length_matters: 'Word Length',
  category_lockout: 'Category Lockout',
};

export const SPECIAL_ROUND_DESCRIPTIONS = {
  first_double: 'All scores are doubled this round!',
  first_half: 'All scores are halved this round.',
  time_zones: 'Submit in under 10s for +10, under 20s for +5. Over 25s costs -5.',
  double_jeopardy: 'Wrong or blank answers with a non-empty attempt cost -5 points each!',
  wildcard: 'A random multiplier (0.5×, 1.5×, 2× or 3×) is applied to all scores.',
  all_or_nothing: 'Get every category right for 2× score — otherwise you score 0!',
  pyramid: 'Consecutive correct answers build a streak bonus (+3 per answer in a streak).',
  length_matters: 'Valid answers longer than 6 letters earn +2 per extra letter.',
  category_lockout: 'One random category is worth 0 points this round.',
};

/**
 * Randomly assigns special round indices throughout the game.
 * @param {number} totalRounds
 * @param {'quick'|'full'} gameMode
 * @returns {Array<{round: number, type: string}>}
 */
export function assignSpecialRounds(totalRounds, gameMode) {
  const count =
    gameMode === 'quick'
      ? Math.floor(Math.random() * 3) + 1 // 1–3
      : Math.floor(Math.random() * 10) + 3; // 3–12

  const assigned = [];
  const usedRounds = new Set();

  while (assigned.length < Math.min(count, totalRounds - 1)) {
    const roundNum = Math.floor(Math.random() * (totalRounds - 1)) + 2; // Avoid round 1
    if (!usedRounds.has(roundNum)) {
      usedRounds.add(roundNum);
      const type = ROUND_TYPES[Math.floor(Math.random() * ROUND_TYPES.length)];
      assigned.push({ round: roundNum, type });
    }
  }

  return assigned;
}

/**
 * Applies a special round effect to the current round's scores.
 *
 * @param {Object} userScores - { [category]: number }
 * @param {Object} botScores  - { [category]: number }
 * @param {string} type       - special round type
 * @param {number} submissionTimeSecs - seconds remaining when user submitted (0–30)
 * @param {Object} userAnswers - raw user answer strings for length/attempt checks
 * @returns {{ userScores, botScores, effectDescription, multiplier }}
 */
export function applySpecialRound(
  userScores,
  botScores,
  type,
  submissionTimeSecs,
  userAnswers = {}
) {
  const u = { ...userScores };
  const b = { ...botScores };
  let effectDescription = SPECIAL_ROUND_DESCRIPTIONS[type] || 'Special round!';
  let multiplier = null;

  switch (type) {
    case 'first_double': {
      Object.keys(u).forEach((k) => (u[k] = u[k] * 2));
      Object.keys(b).forEach((k) => (b[k] = b[k] * 2));
      break;
    }

    case 'first_half': {
      Object.keys(u).forEach((k) => (u[k] = Math.floor(u[k] / 2)));
      Object.keys(b).forEach((k) => (b[k] = Math.floor(b[k] / 2)));
      break;
    }

    case 'time_zones': {
      // submissionTimeSecs = seconds remaining at submit (higher = faster)
      const secsRemaining = submissionTimeSecs ?? 0;
      let bonus = 0;
      if (secsRemaining >= 20) bonus = 10;
      else if (secsRemaining >= 10) bonus = 5;
      else if (secsRemaining <= 5) bonus = -5;

      if (bonus !== 0) {
        const userTotal = Object.values(u).reduce((a, v) => a + v, 0);
        if (userTotal > 0 || bonus < 0) {
          // Apply bonus to the first answered category, or first category if penalty
          const target =
            Object.keys(u).find((k) => u[k] > 0) || Object.keys(u)[0];
          if (target) u[target] = Math.max(-10, u[target] + bonus);
        }
      }

      effectDescription =
        bonus > 0
          ? `Speed bonus: +${bonus} pts!`
          : bonus < 0
          ? `Slow penalty: ${bonus} pts`
          : 'No speed bonus this time.';
      break;
    }

    case 'double_jeopardy': {
      Object.keys(u).forEach((k) => {
        if (u[k] === 0 && userAnswers[k]?.trim()) u[k] = -5;
      });
      Object.keys(b).forEach((k) => {
        // Bot: penalise if it had a word but scored 0 (invalid word)
        if (b[k] === 0 && botScores[k] === 0) b[k] = 0; // no way to know — skip penalty for bot
      });
      break;
    }

    case 'wildcard': {
      const options = [0.5, 1.5, 2, 3];
      multiplier = options[Math.floor(Math.random() * options.length)];
      Object.keys(u).forEach((k) => (u[k] = Math.floor(u[k] * multiplier)));
      Object.keys(b).forEach((k) => (b[k] = Math.floor(b[k] * multiplier)));
      effectDescription = `Wildcard! ${multiplier}× multiplier applied!`;
      break;
    }

    case 'all_or_nothing': {
      const categories = Object.keys(u);
      const maxPossible = categories.length * 10;
      const userTotal = Object.values(u).reduce((a, v) => a + v, 0);
      const botTotal = Object.values(b).reduce((a, v) => a + v, 0);

      if (userTotal === maxPossible) {
        categories.forEach((k) => (u[k] = u[k] * 2));
        effectDescription = 'Perfect! Your score doubled!';
      } else {
        categories.forEach((k) => (u[k] = 0));
        effectDescription = 'Not perfect — all your points wiped!';
      }

      if (botTotal === maxPossible) {
        categories.forEach((k) => (b[k] = b[k] * 2));
      } else {
        categories.forEach((k) => (b[k] = 0));
      }
      break;
    }

    case 'pyramid': {
      const categories = Object.keys(u);
      let streak = 0;
      let bonus = 0;
      categories.forEach((k) => {
        if (u[k] > 0) {
          streak++;
          bonus += streak * 3;
        } else {
          streak = 0;
        }
      });
      if (bonus > 0) {
        const last = [...categories].reverse().find((k) => u[k] > 0);
        if (last) u[last] += bonus;
        effectDescription = `Streak bonus: +${bonus} pts!`;
      } else {
        effectDescription = 'No streak built this round.';
      }
      break;
    }

    case 'length_matters': {
      Object.keys(u).forEach((k) => {
        const word = userAnswers[k]?.trim() || '';
        if (u[k] > 0 && word.length > 6) {
          u[k] += (word.length - 6) * 2;
        }
      });
      break;
    }

    case 'category_lockout': {
      const categories = Object.keys(u);
      const locked = categories[Math.floor(Math.random() * categories.length)];
      u[locked] = 0;
      b[locked] = 0;
      effectDescription = `"${locked.charAt(0).toUpperCase() + locked.slice(1)}" locked — worth 0 this round!`;
      break;
    }

    default:
      break;
  }

  return { userScores: u, botScores: b, effectDescription, multiplier };
}
